import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalProps,
  Button,
  Flex,
  Stack,
  ModalContentProps,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router";
import React, { PropsWithChildren, ReactNode, useCallback, useMemo } from "react";

interface FormWrapperProps extends PropsWithChildren {
  onClick: () => void;
  buttonText?: string;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  mt?: string | number;
}

export function FormWrapper({
  children, onClick, buttonText = "Close", buttonDisabled = false, buttonLoading = false, mt,
}: FormWrapperProps) {
  return (
    <Stack mt={mt}>
      {children}
      <Flex w="100%" justify="flex-end">
        <Button onClick={onClick} isDisabled={buttonDisabled} isLoading={buttonLoading}>{buttonText}</Button>
      </Flex>
    </Stack>
  );
}

interface V2ModalFormProps extends Partial<ModalProps> {
  // eid represents the id of the form being edited
  eid?: string;
  modalHeader?: string | ReactNode;
  renderButton: (onOpen: () => void) => ReactNode;
  renderForm: (onClose: () => void) => ReactNode;
  includeCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  modalContentProps?: ModalContentProps;
}

export default function V2ModalForm({
  eid = undefined,
  modalHeader,
  renderButton,
  renderForm,
  includeCloseButton = true,
  closeOnOverlayClick = false,
  modalContentProps = {},
  ...rest
}: V2ModalFormProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultIsOpen = useMemo(() => searchParams.has("eid") && searchParams.get("eid") === eid, [eid, searchParams]);

  const wrappedOnClose = useCallback(() => {
    if (eid && searchParams.has("eid") && searchParams.get("eid") === eid) searchParams.delete("eid");
    setSearchParams(searchParams);
    rest?.onClose?.();
  }, [eid, rest, searchParams, setSearchParams]);

  const wrappedOnOpen = useCallback(() => {
    if (eid && (!searchParams.has("eid") || (searchParams.has("eid") && searchParams.get("eid") !== eid))) setSearchParams({ eid });
  }, [eid, searchParams, setSearchParams]);

  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen, onClose: wrappedOnClose, onOpen: wrappedOnOpen });

  const initialRef = React.useRef(null);

  return (
    <>
      {renderButton(onOpen)}

      <Modal initialFocusRef={initialRef} closeOnOverlayClick={closeOnOverlayClick} {...rest} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent {...modalContentProps}>
          {modalHeader && <ModalHeader fontSize={24}>{modalHeader}</ModalHeader>}
          {includeCloseButton && <ModalCloseButton size="sm" />}
          <ModalBody pb={6}>{renderForm(onClose)}</ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
